// UPPDRAG-51 — ball-holder rotation algorithm.
//
// Pure helpers (no I/O), so vitest can pin every branch and the
// publishMutation in AdminLeagues can call this without async setup.
//
// 3 principles (mirrors the leagues.ball_holder_principle CHECK enum):
//
//   alternating_pair (default) — alternate between the home team's two
//                                players each match within a round; rounds
//                                alternate which player starts so even/odd
//                                rounds get balanced
//   by_match_number            — distribute evenly across all 4 players
//                                (home p1+p2, away p1+p2) using the match
//                                index within its round modulo 4
//   club_provides              — return null for every match
//
// Edge cases:
//   - team.player2 may be NULL (single-player team, pending invite,
//     player removed) — fall back to player1
//   - both player1 and player2 NULL — return null for that match
//   - round may be NULL — treat as round 0 for parity calculations
//
// PR-1b (2026-05-12): the player slots now carry a discriminated
// `BallHolderRef` (kind: 'user' | 'customer') instead of raw user_ids.
// This enables the dual-FK pattern for customer-only players (per
// audit-reports/investigations/customer-player-architecture-findings.md).
// The picker logic is otherwise unchanged — only the carrier type swaps.
//
// TASK-67 PR-D: the BallHolderTeam shape (player1 / player2 slots) is
// preserved as the input contract, but production callers no longer read
// those columns from the teams row. Instead they derive the "first 2
// primaries by joined_at" pair from team_members via
// `extractPrimariesForBallHolder` (lib/leagueScheduling.ts) and project
// the refs into this shape before invoking pickBallHolder. Keeping the
// pure signature unchanged avoids churning the AdminLeagues / MatchDetail
// / reminder callers in the same PR — they construct the shape from
// `extractPrimariesForBallHolder(team.members)` and pass it in here.

export type BallHolderPrinciple =
  | "alternating_pair"
  | "by_match_number"
  | "club_provides";

// ─── PR-1b: discriminated identity reference ──────────────────────────
//
// A single primary slot can carry either a profile-linked Boply account
// (`kind: 'user'`) or a CRM-only customer (`kind: 'customer'`). Mirrors
// the dual-FK shape established by PR-1a's
// `league_matches.ball_holder_user_id` + `ball_holder_customer_id`
// columns + the `team_members` table's `user_id` / `customer_id` pair.
//
// `pickBallHolder` returns this shape; the caller in
// `leagueScheduling.ts` decides which DB column to write based on
// `kind`. PR-1b's write adapter currently NULLs the customer-kind case
// (preserving pre-PR-1b observable behavior). PR-1c flips the fence and
// writes to `ball_holder_customer_id` for customer-kind refs.

export type BallHolderRef =
  | { kind: "user"; id: string }
  | { kind: "customer"; id: string };

export interface BallHolderTeam {
  id: string;
  /**
   * TASK-67 PR-D + PR-1b: the FIRST primary team_members row (sorted by
   * joined_at). Carries either user_id or customer_id via discriminated
   * `BallHolderRef`. Callers compute this via
   * `extractPrimariesForBallHolder(team.members).primary1`.
   */
  player1: BallHolderRef | null;
  /**
   * TASK-67 PR-D + PR-1b: the SECOND primary team_members row. Same shape.
   */
  player2: BallHolderRef | null;
}

export interface BallHolderMatch {
  id: string;
  round: number | null;
  /** Index of this match within its round (0-based). */
  match_index_in_round: number;
  home_team: BallHolderTeam;
  away_team: BallHolderTeam;
}

/**
 * Compute the ball-holder ref for a single match.
 * Returns null when the principle is "club_provides" or when no player
 * is available for the chosen slot (defensive fallback).
 *
 * PR-1b: return type changed from `string | null` to
 * `BallHolderRef | null`. Logic preserved per principle.
 */
export function pickBallHolder(
  match: BallHolderMatch,
  principle: BallHolderPrinciple,
): BallHolderRef | null {
  if (principle === "club_provides") return null;

  if (principle === "alternating_pair") {
    // Even rounds → home.player1, odd rounds → home.player2.
    // Within a round, alternate further by match_index_in_round so multiple
    // matches in the same round share the load between the two players.
    const round = match.round ?? 0;
    const useFirst = (round + match.match_index_in_round) % 2 === 0;
    if (useFirst) {
      return match.home_team.player1 ?? match.home_team.player2 ?? null;
    }
    return match.home_team.player2 ?? match.home_team.player1 ?? null;
  }

  // by_match_number: cycle through all 4 players in the match
  // [home.p1, away.p1, home.p2, away.p2] so consecutive matches in the
  // round rotate evenly across both teams.
  const candidates: (BallHolderRef | null)[] = [
    match.home_team.player1,
    match.away_team.player1,
    match.home_team.player2,
    match.away_team.player2,
  ];
  const startIdx = match.match_index_in_round % 4;
  // Walk the rotation starting at startIdx — if the chosen slot is null,
  // try the next one. Returns null only if all 4 slots are null.
  for (let i = 0; i < 4; i++) {
    const idx = (startIdx + i) % 4;
    if (candidates[idx]) return candidates[idx];
  }
  return null;
}

/**
 * Compute ball-holder refs for a batch of matches.
 * Returns a Map<match_id, BallHolderRef | null>.
 *
 * Caller is responsible for grouping matches by round and assigning
 * stable match_index_in_round (e.g. by sorted match.id within the round
 * so re-runs are deterministic).
 *
 * PR-1b: value type changed from `string | null` to `BallHolderRef | null`.
 */
export function pickBallHoldersForMatches(
  matches: BallHolderMatch[],
  principle: BallHolderPrinciple,
): Map<string, BallHolderRef | null> {
  const out = new Map<string, BallHolderRef | null>();
  for (const m of matches) {
    out.set(m.id, pickBallHolder(m, principle));
  }
  return out;
}
